import { CLOUDFLARE_URL } from "@/lib/config";
import { useCommunication } from "../lib/CommunicationWrapper";
import { AssignmentPlanned } from "../lib/types";
import { ChangeLogItem, PlannedAssignmentCache, tableNameMap } from "../lib/types";


export const useAssignmentSync = () => {
    const { getOptions, updateOptions, authenticatedClient, showError, sendMessage, sendMessageAndListen, authenticatedFetch, openGatedDialog } = useCommunication();


    const updatePlannedCache = async (assignments: AssignmentPlanned[] | Partial<AssignmentPlanned>[], action: "add" | "delete" | "update") => {
        console.log("[updatePlannedCache] Called with action:", action, "assignments:", assignments);
        const local = getOptions("local");
        const cachedData: PlannedAssignmentCache = local["cached_planned_assignments"] ?? {};

        assignments.forEach((assignment: AssignmentPlanned | Partial<AssignmentPlanned>) => {
            const assignmentId = assignment.id;
            if (!assignmentId) return;

            if (action === "add") {
                cachedData[assignmentId] = assignment as AssignmentPlanned;
            } else if (action === "update") {
                cachedData[assignmentId] = {
                    ...cachedData[assignmentId],
                    ...assignment,
                };
            } else if (action === "delete") {
                delete cachedData[assignmentId];
            }
        });

        //await chrome.storage.local.set({ "cached_planned_assignments": cachedData });
        console.log("[updatePlannedCache] Writing to local storage:", cachedData);
        updateOptions({ "cached_planned_assignments": cachedData }, "local");

    }

    const upsertPlannedAssignment = async (assignments: AssignmentPlanned[] | Partial<AssignmentPlanned>[]) => {

        console.log("[upsertPlannedAssignment] Called with assignments:", assignments);

        // Map camelCase to snake_case for the API
        const mapped = assignments.map((assignment: AssignmentPlanned | Partial<AssignmentPlanned>) => mapAssignmentToApi(assignment));

        const response = await authenticatedFetch(`${CLOUDFLARE_URL}/assignments/upsert`, {
            method: "POST",
            body: {
                assignments: mapped
            },
        });

        // Wrap in array for the new API
        const { message, error } = await response.json();

        // Handle usage limit errors (429 status)
        if (error?.code === 429 || response.status === 429) {
            openGatedDialog("assignments_upsert");
            throw new Error("gated");
        }

        if (error && error.includes("gated")) {
            throw new Error("gated");
        }

        for (const assignment of assignments) {
            syncAssignmentToCalendars({
                assignmentId: assignment.id.toString(),
                assignmentData: assignment,
                syncType: 'planned'
            });
        }

        // Return message with usage data
        return message;
    }

    const deletePlannedAssignments = async (assignmentIds: string[], options: { showError?: boolean } = {}) => {
        const results = [];
        let lastUsage = null;
        const showErrors = options.showError !== false;

        for (const assignmentId of assignmentIds) {
            let message: any = null;
            let parsedPayload: any = null;

            try {
                const response = await authenticatedFetch(`${CLOUDFLARE_URL}/assignments/${assignmentId}`, {
                    method: "DELETE",
                });

                const bodyText = await response.text();
                if (bodyText.trim().length > 0) {
                    try {
                        parsedPayload = JSON.parse(bodyText);
                    } catch (parseError) {
                        console.warn(`Failed to parse delete response for ${assignmentId}:`, parseError);
                    }
                }

                const apiError = parsedPayload?.error;
                const is404 = response.status === 404;

                // Treat 404 as success - assignment doesn't exist means deletion "succeeded"
                if (!response.ok && !is404) {
                    const errorMsg = apiError || `HTTP ${response.status}`;
                    console.error(`Error deleting assignment ${assignmentId}:`, errorMsg);
                    if (showErrors) {
                        showError("There was an error deleting the assignment", "error");
                    }
                    results.push({ id: assignmentId, success: false });
                    continue;
                }

                // Only fail on actual errors (not 404, not missing assignment)
                if (apiError && !apiError.toLowerCase?.().includes('not found')) {
                    console.error(`Error deleting assignment ${assignmentId}:`, apiError);
                    if (showErrors) {
                        showError("There was an error deleting the assignment", "error");
                    }
                    results.push({ id: assignmentId, success: false });
                    continue;
                }

                message = parsedPayload?.message;
            } catch (error) {
                console.error(`Failed to delete assignment ${assignmentId}:`, error);
                if (showErrors) {
                    showError("There was an error deleting the assignment", "error");
                }
                results.push({ id: assignmentId, success: false });
                continue;
            }

            try {
                await deleteAssignmentFromCalendars(assignmentId);
            } catch (calendarError) {
                console.error(`Failed to remove assignment ${assignmentId} from calendars:`, calendarError);
            }

            if (message?.usage) {
                lastUsage = message.usage;
            }

            results.push({ id: assignmentId, success: true });
        }

        // Return object with count and usage data
        return {
            count: results.filter(r => r.success).length,
            usage: lastUsage
        };
    }

    const syncAssignmentToCalendars = async ({ assignmentId, assignmentData, syncType }: { assignmentId: string, assignmentData: any, syncType: 'planned' | 'dueDate' | 'both' }) => {
        return await sendMessageAndListen({
            type: "CALL_EXTENSION_FUNC",
            payload: {
                func: "syncAssignmentToCalendars",
                args: { assignmentId, assignmentData, syncType },
            },
        });
    }

    const triggerCalendarPullSync = async () => {
        return await sendMessageAndListen({
            type: "CALL_EXTENSION_FUNC",
            payload: {
                func: "triggerCalendarPullSync",
                args: {},
            },
        });
    }

    const deleteAssignmentFromCalendars = async (assignmentId: string) => {
        return await sendMessageAndListen({
            type: "CALL_EXTENSION_FUNC",
            payload: {
                func: "deleteAssignmentFromCalendars",
                args: { assignmentId },
            },
        });
    }

    const getWeeklyUsage = async () => {
        const response = await authenticatedFetch(`${CLOUDFLARE_URL}/assignments/weekly-usage`, {
            method: "GET",
        });

        const { message, error } = await response.json();

        if (error) {
            console.error("Error fetching weekly usage:", error);
            throw new Error(error);
        }

        return message;
    }


    return {
        updatePlannedCache,
        upsertPlannedAssignment,
        deletePlannedAssignments,
        // calendar sync funcs
        syncAssignmentToCalendars,
        triggerCalendarPullSync,
        deleteAssignmentFromCalendars,
        getWeeklyUsage,
    };
}

export function mapAssignmentToApi(assignment: AssignmentPlanned | Partial<AssignmentPlanned>) {
    const mapped: any = {};
    if (assignment.id !== undefined) {
        mapped.assignment_id = assignment.id;
    }
    if (assignment.course !== undefined) {
        mapped.course = assignment.course;
    }
    if (assignment.title !== undefined) {
        mapped.title = assignment.title;
    }
    if (assignment.duration !== undefined) {
        mapped.duration = assignment.duration;
    }
    if (assignment.plannedAt !== undefined) {
        mapped.planned_at = assignment.plannedAt;
    }
    if (assignment.status !== undefined) {
        mapped.status = assignment.status;
    }
    return mapped;
}

// update the local cache based on change log items
export function processPlannedAssignmentChange(storage: any, item: ChangeLogItem) {
    const storageKey = tableNameMap["planned_assignments"];
    storage[storageKey] ??= {};
    const cache: PlannedAssignmentCache = storage[storageKey];
    const assignmentId = item?.metadata?.assignment_id;
    if (!assignmentId) return;

    item.metadata.id = assignmentId;


    if (item.operation === "INSERT" || item.operation === "UPDATE") {
        if (item.metadata.planned_at) {
            item.metadata.plannedAt = item.metadata.planned_at;
        }
        cache[assignmentId] = item.metadata as AssignmentPlanned;
    } else if (item.operation === "DELETE") {
        delete cache[assignmentId];
    }
}
